import { Box, Card, CardProps, Grid, GridProps, Stack, Typography, TypographyProps } from '@mui/material'
import { format } from 'date-fns'
import React from 'react'
import { TooltipProps } from 'recharts'
import useIsMobile from '../../hooks/useIsMobile'
import { formatDollarValue } from './chartUtils'

const TableRowBase: React.FC<GridProps> = (props) => {
    return <Grid container wrap='nowrap' columnGap={2} {...props} sx={{ ...props.sx }} />
}
export const TableHeaderRow: React.FC<GridProps> = (props) => {
    const isMobile = useIsMobile()

    return <TableRowBase sx={{ py: !isMobile ? 1 : 0.75, px: isMobile ? 2 : 3, ...props.sx }} {...props} />
}
export const TableRow: React.FC<GridProps> = ({ children, onClick, className = '' }) => (
    <div onClick={onClick} className={`${className} pb-4 pt-4 px-4 border-b-[1px] border-[#0C3F6D]`}>
        <TableRowBase>{children}</TableRowBase>
    </div>
)

export const TableCell: React.FC<GridProps & { main?: boolean | number; floatRight?: boolean; floatLeft?: boolean }> = ({ main, floatRight, floatLeft, sx, ...props }) => {
    const isMobile = useIsMobile()
    return (
        <Grid
            item
            xs={main ? main : false}
            {...props}
            sx={{
                ...(floatRight && {
                    justifyContent: 'flex-end',
                    // exclude the left-most "floatRight" element
                    ':not(.MuiGrid-grid-xs-true + &)': {
                        minWidth: !isMobile ? 160 : ''
                    }
                }),
                ...(floatLeft && {
                    justifyContent: 'flex-start',
                    // exclude the left-most "floatRight" element
                    ':not(.MuiGrid-grid-xs-true + &)': {
                        minWidth: !isMobile ? 160 : ''
                    }
                }),
                display: 'flex',
                alignItems: 'center',
                ...sx
            }}
        />
    )
}
export const TableHeaderText: React.FC<TypographyProps> = (props) => {
    const isMobile = useIsMobile()
    return <Typography fontSize={!isMobile ? 18 : 14} fontWeight={500} {...props} />
}

export const TableText: React.FC<TypographyProps> = (props) => {
    const isMobile = useIsMobile()

    return <Typography fontSize={!isMobile ? 18 : 14} fontWeight={400} {...props} />
}

export const MyCard: React.FC<CardProps> = (props) => {
    const isMobile = useIsMobile()

    return (
        <Card
            {...props}
            sx={{
                ...props.sx,
                py: isMobile ? 2 : 3,
                px: isMobile ? 2 : 3,
                boxShadow: 2,
                backgroundColor: '#201e43',
                borderRadius: isMobile ? 0.75 : 1
            }}></Card>
    )
}

export const MyContainer = (props: any) => {
    const isMobile = useIsMobile()

    return (
        <Box
            {...props}
            sx={{
                p: isMobile ? 1 : 3,
                border: '1px solid',
                borderColor: '#4E4E5A',
                boxShadow: 40,
                borderRadius: isMobile ? 1 : 1.5,
                backgroundColor: '#0d0c1e',
                width: '100%'
            }}
        />
    )
}

export const CustomTooltip = ({ active, payload, label, mode = 'DAY' }: { active?: any; payload?: any; label?: any; mode?: 'DAY' | 'WEEK' }) => {
    const value = payload?.value ?? payload?.[0]?.value
    const timestamp = Number(label) * 1000
    const isMobile = useIsMobile()

    if (!active || !value || !label || !timestamp) return <></>

    const getTime = () => {
        if (mode === 'DAY') {
            return `${format(timestamp, 'LLL')} ${format(timestamp, 'd')}, ${format(timestamp, 'y')}`
        }

        const startLabel = `${format(timestamp, 'LLL')} ${format(timestamp, 'd')}`
        const endOfWeek = timestamp + 7 * 24 * 60 * 60 * 1000
        const endLabel = endOfWeek >= Date.now() ? 'now' : `${format(endOfWeek, 'LLL')} ${format(endOfWeek, 'd')}`
        return `${startLabel} - ${endLabel}`
    }

    const time = getTime()

    return (
        <Stack
            boxShadow={5}
            sx={{
                backgroundColor: '#0C3F6D',
                color: '#549EFC',
                borderRadius: !isMobile ? 0.5 : 0.2,
                px: !isMobile ? 1 : 0.5,
                py: !isMobile ? 1 : 0.25,
                alignItems: 'center'
            }}>
            <Typography fontSize={!isMobile ? 18 : 14} fontWeight={500}>{`$${formatDollarValue(value)}`}</Typography>
            <Typography fontSize={12} fontWeight={400}>{`${time}`}</Typography>
        </Stack>
    )
}

export const commonTooltipProps = (isMobile: boolean): Partial<TooltipProps<any, any>> => ({
    wrapperStyle: { outline: 0 },
    content: CustomTooltip,
    isAnimationActive: false,
    position: { y: 0 },
    offset: !isMobile ? -44 : -40
})

export const NUMBER_COLUMN_MIN_WIDTH = 22
